import { createSlice } from "@reduxjs/toolkit";
import {
    getCourseChatHistory,
    getCourseDetails,
    listCourses,
    listMyCourses,
    markChapterProgress,
    sendMessageToAiTutor,
    submitModuleAssessment,
    submitQuickQuiz,
    getQuizDrafts,
    clearQuizDraft,
} from "../thunks/courseThunk";

const normalizeChatHistory = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.results)) return payload.results;
    if (Array.isArray(payload?.messages)) return payload.messages;
    if (Array.isArray(payload?.chat_history)) return payload.chat_history;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
};

const courseSlice = createSlice({
    name: "course",
    initialState: {
        courses: [],
        enrolledCourses: [],
        myCourses: [],
        courseDetailsData: null,
        courseDetails: null,
        chapterProgress: null,
        chatHistory: [],
        aiMessage: null,
        quickQuizSubmitResult: null,
        quickQuizSubmitLoading: false,
        quickQuizSubmitError: null,
        quizDrafts: [],
        loading: false,
        error: null,
    },
    reducers: {
        clearQuickQuizSubmit: (state) => {
            state.quickQuizSubmitResult = null;
            state.quickQuizSubmitLoading = false;
            state.quickQuizSubmitError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(listCourses.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(listCourses.fulfilled, (state, action) => {
                state.loading = false;
                state.courses = action.payload;
            })
            .addCase(listCourses.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.detail || "Failed to load courses.";
            })
            .addCase(listMyCourses.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(listMyCourses.fulfilled, (state, action) => {
                state.loading = false;
                state.enrolledCourses = action.payload;
                state.myCourses = action.payload;
            })
            .addCase(listMyCourses.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.detail || "Failed to load enrolled courses.";
            })
            .addCase(getCourseDetails.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getCourseDetails.fulfilled, (state, action) => {
                state.loading = false;
                state.courseDetailsData = action.payload;
                state.courseDetails = action.payload;
            })
            .addCase(getCourseDetails.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.detail || "Failed to load course details.";
            })
            .addCase(markChapterProgress.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(markChapterProgress.fulfilled, (state, action) => {
                state.loading = false;
                const { chapterId, completed } = action.payload;

                const domains = state.courseDetailsData?.domains || [];
                for (const domain of domains) {
                    const chapter = domain.chapters?.find((item) => String(item.id) === String(chapterId));
                    if (chapter) {
                        chapter.is_completed = completed;
                        chapter.completed = completed;
                        break;
                    }
                }
            })
            .addCase(markChapterProgress.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.detail || "Failed to update chapter progress.";
            })
            .addCase(getCourseChatHistory.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getCourseChatHistory.fulfilled, (state, action) => {
                state.loading = false;
                state.chatHistory = normalizeChatHistory(action.payload);
            })
            .addCase(getCourseChatHistory.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.detail || "Failed to load chat history.";
            })
            .addCase(sendMessageToAiTutor.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(sendMessageToAiTutor.fulfilled, (state, action) => {
                state.loading = false;
                state.aiMessage = action.payload;
                const newMessages = normalizeChatHistory(action.payload);
                if (newMessages.length > 0) {
                    state.chatHistory = newMessages;
                }
            })
            .addCase(sendMessageToAiTutor.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.detail || "Failed to send message to AI tutor.";
            })
            .addCase(submitQuickQuiz.pending, (state) => {
                state.quickQuizSubmitLoading = true;
                state.quickQuizSubmitError = null;
                state.quickQuizSubmitResult = null;
            })
            .addCase(submitQuickQuiz.fulfilled, (state, action) => {
                state.quickQuizSubmitLoading = false;
                state.quickQuizSubmitError = null;
                state.quickQuizSubmitResult = action.payload;

                // Keep Resources locks/readiness UI in sync before getCourseDetails returns.
                const chapterId = action.payload?.chapter_id;
                const passed = Boolean(action.payload?.passed);
                if (chapterId == null) return;
                const domains = state.courseDetailsData?.domains || [];
                for (const domain of domains) {
                    const chapter = domain.chapters?.find((item) => String(item.id) === String(chapterId));
                    if (chapter) {
                        chapter.quiz_attempted = true;
                        if (passed) {
                            chapter.quiz_completed = true;
                        }
                        break;
                    }
                }
            })
            .addCase(submitModuleAssessment.pending, (state) => {
                state.quickQuizSubmitLoading = true;
                state.quickQuizSubmitError = null;
                state.quickQuizSubmitResult = null;
            })
            .addCase(submitModuleAssessment.fulfilled, (state, action) => {
                state.quickQuizSubmitLoading = false;
                state.quickQuizSubmitError = null;
                state.quickQuizSubmitResult = action.payload;
            })
            .addCase(submitModuleAssessment.rejected, (state, action) => {
                state.quickQuizSubmitLoading = false;
                state.quickQuizSubmitError =
                    action.payload?.detail || action.payload?.error || action.payload || "Failed to submit module assessment.";
            })
            .addCase(submitQuickQuiz.rejected, (state, action) => {
                state.quickQuizSubmitLoading = false;
                state.quickQuizSubmitError =
                    action.payload?.detail ||
                    action.payload?.error ||
                    action.payload?.message ||
                    "Failed to submit quick quiz.";
            })
            .addCase(getQuizDrafts.fulfilled, (state, action) => {
                state.quizDrafts = action.payload?.drafts || [];
            })
            .addCase(clearQuizDraft.fulfilled, (state, action) => {
                state.quizDrafts = (state.quizDrafts || []).filter(
                    (draft) =>
                        !(draft.quiz_type === action.payload.quizType && Number(draft.target_id) === Number(action.payload.targetId))
                );
            });
    },
});

export const { clearQuickQuizSubmit } = courseSlice.actions;

export default courseSlice.reducer;
